import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js';
import type { StripeElementStyle } from '@stripe/stripe-js';
import { subscribeNewsletter } from '../../src/forms/simpleCheckout/api';
import type { ContractSuccess, Country, PaymentMethod, Salutation } from '../../src/forms/simpleCheckout/types';
import { useSimpleCheckout } from '../../src/forms/simpleCheckout/useSimpleCheckout';
import { CardBrandLogos } from './CardBrandLogos';
import { trackAddPaymentInfo, trackPurchase, type DetectedProduct } from './tracking';
import type { OpenOptions } from './types';

// 16px keeps iOS Safari from auto-zooming on focus, which would otherwise
// shift our modal layout and hide whichever Stripe field the user just tapped.
const cardStyle: StripeElementStyle = {
  base: {
    fontSize: '16px',
    color: '#1a1a1a',
    '::placeholder': { color: '#9ca3af' },
  },
  invalid: { color: '#E6393C' },
};

// Inline styles (not Tailwind classes) so the border/background survive host
// themes whose generic `div { … }` rules would otherwise strip them.
const cardFieldStyle: CSSProperties = {
  border: '1px solid #d2d2d9',
  background: '#ffffff',
  padding: '8px 14px',
};

// text-base (16px) is kept so iOS Safari doesn't auto-zoom on focus, but
// we trim vertical padding to py-2 to keep the modal compact — the form
// has a lot of rows and the tall inputs were pushing the submit button
// off-screen on smaller phones.
const inputCls =
  'w-full border border-secondary-dark bg-white px-3 py-2 text-base text-black placeholder:text-gray-medium focus:border-primary focus:outline-none transition-colors duration-200';

const SALUTATION_LABELS: Record<Salutation, string> = {
  HERR: 'Herr',
  FRAU: 'Frau',
  FIRMA: 'Firma',
};

const COUNTRY_LABELS: Record<Country, string> = {
  AT: 'Österreich',
  DE: 'Deutschland',
  CH: 'Schweiz',
};

const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  INVOICE: 'Auf Rechnung bestellen',
  STRIPE: 'Mit Kreditkarte bestellen',
};

// Two-option payment selector at the top of the payment section. INVOICE is
// listed first per marketing. Inline-styled radio buttons so host themes
// can't restyle the box / outline.
function PaymentMethodPicker({ value, onChange }: { value: PaymentMethod; onChange: (next: PaymentMethod) => void }) {
  const options: PaymentMethod[] = ['INVOICE', 'STRIPE'];
  return (
    <fieldset style={{ border: 0, margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <legend
        style={{
          fontSize: '14px',
          fontWeight: 600,
          color: '#000000',
          marginBottom: 2,
          padding: 0,
          fontFamily: 'inherit',
        }}
      >
        Zahlungsart
      </legend>
      {options.map((opt) => {
        const selected = value === opt;
        return (
          <label
            key={opt}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              cursor: 'pointer',
              border: `1px solid ${selected ? '#000000' : '#d2d2d9'}`,
              background: '#ffffff',
              padding: '8px 14px',
              fontSize: '15px',
              color: '#000000',
              fontFamily: 'inherit',
            }}
          >
            <input
              type="radio"
              name="wco-paymentMethod"
              value={opt}
              checked={selected}
              onChange={() => onChange(opt)}
              style={{ accentColor: '#E6393C', width: 16, height: 16, margin: 0 }}
            />
            <span>{PAYMENT_LABELS[opt]}</span>
          </label>
        );
      })}
    </fieldset>
  );
}

// Address block heading, styled like the "Zahlungsart" legend. Inline
// styles so host themes can't restyle it.
function SectionHeading({ children }: { children: string }) {
  return (
    <div style={{ fontSize: '14px', fontWeight: 600, color: '#000000', marginBottom: -4, fontFamily: 'inherit' }}>
      {children}
    </div>
  );
}

// Disclosure row that toggles a separate billing-address form. Closed by
// default — Compax uses the shipping block for both addresses until the
// user explicitly opts in.
function BillingAddressDisclosure({
  open,
  onToggle,
  label = 'Andere Rechnungsadresse',
}: {
  open: boolean;
  onToggle: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: 'transparent',
        border: 0,
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        fontSize: '14px',
        color: '#000000',
        fontFamily: 'inherit',
        textAlign: 'left',
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 12 12"
        aria-hidden="true"
        style={{
          width: 10,
          height: 10,
          flexShrink: 0,
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s',
        }}
        fill="currentColor"
      >
        <path d="M3 1l6 5-6 5z" />
      </svg>
      <span style={{ textDecoration: 'underline' }}>{label}</span>
    </button>
  );
}

// Optional Gutschein: collapsed by default, checked at Compax on "Einlösen";
// once applied it shows the code with a way to remove it.
function PromoCodeField({
  applied,
  onApply,
}: {
  applied: string | null;
  onApply: (code: string) => Promise<{ success: boolean; error?: string }>;
}) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = async () => {
    const trimmed = code.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    const result = await onApply(trimmed);
    setBusy(false);
    if (result.success) setCode('');
    else setError(result.error || 'Ungültiger Gutschein-Code');
  };

  if (applied) {
    return (
      <p className="flex items-center gap-2 text-sm text-black">
        <span>
          Gutschein <strong>{applied}</strong> eingelöst
        </span>
        <button type="button" onClick={() => onApply('')} className="text-primary underline">
          entfernen
        </button>
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <BillingAddressDisclosure open={open} onToggle={() => setOpen(!open)} label="Gutschein-Code einlösen" />
      {open && (
        <>
          <div className="flex gap-3">
            <input
              id="wco-promo"
              type="text"
              autoComplete="off"
              placeholder="Gutschein-Code"
              aria-label="Gutschein-Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              // Enter would submit the whole order form
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  apply();
                }
              }}
              className={inputCls}
            />
            <button
              type="button"
              onClick={apply}
              disabled={busy || !code.trim()}
              className="shrink-0 border border-primary px-4 text-sm font-semibold uppercase tracking-wide text-primary transition-colors hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? 'Prüfe …' : 'Einlösen'}
            </button>
          </div>
          {error && <p className="text-sm text-primary">{error}</p>}
        </>
      )}
    </div>
  );
}

// Optional Geburtsdatum input. HTML5 date inputs ignore `placeholder`
// and show the browser's format hint instead, which confuses buyers
// about what the field is for. Earlier we tried a text→date type-swap
// on focus to surface a placeholder, but iOS Safari doesn't reliably
// re-attach the native picker after a type change. So: always
// type="date", with a tiny visible "Geburtsdatum (optional)" hint
// rendered above the input — works on every browser, no JS state.
function BirthdayInput({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  return (
    <label htmlFor="wco-birthday" className="flex flex-col gap-1">
      <span className="text-xs text-gray-medium">Geburtsdatum</span>
      <input
        id="wco-birthday"
        type="date"
        autoComplete="bday"
        aria-label="Geburtsdatum"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        // `appearance-none block` is needed for iOS Safari: without it the
        // date input shrinks to content width AND ignores our text colour,
        // so an empty field renders today's date in solid black (looks
        // pre-filled). Resetting appearance lets our `w-full` and
        // `text-gray-medium` actually take effect.
        className={`${inputCls} block appearance-none ${value ? '' : 'text-gray-medium'}`}
      />
    </label>
  );
}

// Optional WOMAN Front Row newsletter opt-in. Ticking it subscribes the
// buyer via Brevo double opt-in (a confirmation mail is sent after the
// order). Shown above the marketing opt-in. Independent of the purchase
// — the subscription is fired best-effort once the order succeeds.
function NewsletterOptIn({ checked, onChange }: { checked: boolean; onChange: (next: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-start gap-2 text-sm leading-relaxed text-black">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 shrink-0" />
      <span>
        Ja, ich möchte den kostenlosen WOMAN Front Row Newsletter abonnieren. Ihr Platz in der ersten Reihe: sorgfältig
        kuratierte Trends und Tagesthemen, aktuelle Buch- und Hörtipps, angesagte Rezepte, Life-Hacks, Gewinnspiele
        u.v.m. Immer Montag und Freitag in Ihrem Posteingang.
      </span>
    </label>
  );
}

// DSGVO-required affirmative marketing opt-in. The checkbox stays visible
// (so the buyer sees the choice exists) but the long legal block hides
// behind a "Weitere Details" toggle by default — keeps the modal short
// enough that the submit button doesn't get pushed off-screen. Wording
// mirrors the news app's Newsletter promoDisclaimer (the canonical
// up-to-date VGN-group marketing-consent text); when legal updates it
// there, mirror it here too.
function MarketingOptIn({ checked, onChange }: { checked: boolean; onChange: (next: boolean) => void }) {
  const [open, setOpen] = useState(false);
  const toggle = (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        setOpen((v) => !v);
      }}
      aria-expanded={open}
      className="underline"
      style={{
        background: 'transparent',
        border: 0,
        padding: 0,
        cursor: 'pointer',
        color: 'inherit',
        fontFamily: 'inherit',
        fontSize: 'inherit',
      }}
    >
      {open ? 'Weniger anzeigen' : 'Weitere Details'}
    </button>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label className="flex cursor-pointer items-start gap-2 text-sm leading-relaxed text-black">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 shrink-0"
        />
        <span>
          {open ? (
            <>
              Ja, ich möchte personalisierte Werbung der VGN-Gruppe zu Magazinen, Online-Medien, Abos, Gewinnspielen und
              Veranstaltungen per E-Mail, SMS, Telefon und Post erhalten. Dafür dürfen die Unternehmen der VGN-Gruppe –
              die vollständige Liste finden Sie unter{' '}
              <a
                href="https://vgn.at/unternehmensgruppe"
                className="text-coral underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                vgn.at/unternehmensgruppe
              </a>{' '}
              – meine Kontaktdaten und mein Nutzungsverhalten (Abos, Käufe, Interessen, Reaktionen auf Mailings,
              Website- und App-Nutzung) zu einem Werbeprofil verbinden und den Werbeerfolg messen. Widerruf jederzeit an{' '}
              <a href="mailto:datenschutz@vgn.at" className="text-coral underline">
                datenschutz@vgn.at
              </a>
              . Mehr dazu in der{' '}
              <a
                href="https://vgn.at/datenschutzpolicy"
                className="text-coral underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                vgn.at/datenschutzpolicy
              </a>
              . {toggle}
            </>
          ) : (
            <>
              Ja, ich möchte personalisierte Werbung der VGN-Gruppe zu Magazinen, Online-Medien, Abos, Gewinnspielen und
              Veranstaltungen per E-Mail, SMS, Telefon und Post erhalten. {toggle}
            </>
          )}
        </span>
      </label>
      <span className="ml-6 text-xs leading-relaxed text-gray-medium">
        Die Rechtmäßigkeit der Datenverarbeitung bleibt bis zum Zeitpunkt eines allfälligen Widerrufs unberührt. Die
        Einwilligung ist freiwillig. Nähere Informationen zum Datenschutz finden Sie unter{' '}
        <a
          href="https://vgn.at/datenschutzpolicy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-coral underline"
        >
          vgn.at/datenschutzpolicy
        </a>
        .
      </span>
    </div>
  );
}

type SetValue = <K extends keyof FormValuesT>(field: K, value: FormValuesT[K]) => void;
type FormValuesT = {
  billingSalutation: Salutation | null;
  billingFirstName: string;
  billingLastName: string;
  billingStreet: string;
  billingHouseNumber: string;
  billingZip: string;
  billingCity: string;
  billingCountry: Country;
};

// Compact billing form. Mirrors the layout of the main shipping block above
// (salutation → name → street/houseNo → zip/city → country) and shares the
// same inputCls so the look is consistent. Inline-styled wrapping container
// so a host theme can't redraw the borders.
function BillingFields({ values, setValue }: { values: FormValuesT; setValue: SetValue }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 12,
        border: '1px solid #d2d2d9',
        background: '#ffffff',
      }}
    >
      <SectionHeading>Rechnungsadresse</SectionHeading>
      <select
        aria-label="Anrede (Rechnungsadresse)"
        value={values.billingSalutation ?? ''}
        onChange={(e) => setValue('billingSalutation', (e.target.value || null) as Salutation | null)}
        className={`${inputCls} appearance-none bg-no-repeat pr-10 ${values.billingSalutation ? '' : 'text-gray-medium'}`}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%239ca3af' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 8 10 12 14 8'/></svg>\")",
          backgroundPosition: 'right 0.75rem center',
          backgroundSize: '1.25rem 1.25rem',
        }}
        required
      >
        <option value="" disabled>
          Anrede *
        </option>
        {(Object.keys(SALUTATION_LABELS) as Salutation[]).map((id) => (
          <option key={id} value={id} className="text-black">
            {SALUTATION_LABELS[id]}
          </option>
        ))}
      </select>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          type="text"
          required
          autoComplete="given-name"
          placeholder="Vorname *"
          aria-label="Vorname (Rechnungsadresse)"
          value={values.billingFirstName}
          onChange={(e) => setValue('billingFirstName', e.target.value)}
          className={inputCls}
        />
        <input
          type="text"
          required
          autoComplete="family-name"
          placeholder="Nachname *"
          aria-label="Nachname (Rechnungsadresse)"
          value={values.billingLastName}
          onChange={(e) => setValue('billingLastName', e.target.value)}
          className={inputCls}
        />
      </div>

      <div className="grid grid-cols-[2fr_1fr] gap-3">
        <input
          type="text"
          required
          autoComplete="billing address-line1"
          placeholder="Straße *"
          aria-label="Straße (Rechnungsadresse)"
          value={values.billingStreet}
          onChange={(e) => setValue('billingStreet', e.target.value)}
          className={inputCls}
        />
        <input
          type="text"
          required
          placeholder="Hausnr. *"
          aria-label="Hausnummer (Rechnungsadresse)"
          value={values.billingHouseNumber}
          onChange={(e) => setValue('billingHouseNumber', e.target.value)}
          className={inputCls}
        />
      </div>

      <div className="grid grid-cols-[1fr_2fr] gap-3">
        <input
          type="text"
          required
          inputMode="numeric"
          autoComplete="billing postal-code"
          placeholder="PLZ *"
          aria-label="PLZ (Rechnungsadresse)"
          value={values.billingZip}
          onChange={(e) => setValue('billingZip', e.target.value)}
          className={inputCls}
        />
        <input
          type="text"
          required
          autoComplete="billing address-level2"
          placeholder="Ort *"
          aria-label="Ort (Rechnungsadresse)"
          value={values.billingCity}
          onChange={(e) => setValue('billingCity', e.target.value)}
          className={inputCls}
        />
      </div>

      <select
        aria-label="Land (Rechnungsadresse)"
        value={values.billingCountry}
        onChange={(e) => setValue('billingCountry', e.target.value as Country)}
        className={`${inputCls} appearance-none bg-no-repeat pr-10`}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%239ca3af' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 8 10 12 14 8'/></svg>\")",
          backgroundPosition: 'right 0.75rem center',
          backgroundSize: '1.25rem 1.25rem',
        }}
        required
      >
        {(Object.keys(COUNTRY_LABELS) as Country[]).map((code) => (
          <option key={code} value={code} className="text-black">
            {COUNTRY_LABELS[code]}
          </option>
        ))}
      </select>
    </div>
  );
}

type Props = {
  offeringId: string;
  marketingCode: string;
  apiBase: string;
  options: OpenOptions;
  onClose: () => void;
  product: DetectedProduct;
  // App.tsx subscribes to this to re-price the basket (different country
  // means different shipping cost) and flash the new total.
  onCountryChange?: (country: Country) => void;
  // Gutschein state lives in App.tsx, which shows the reduced price in the header.
  promotionalCode?: string | null;
  onApplyPromo?: (code: string) => Promise<{ success: boolean; error?: string }>;
};

export function CheckoutFormBody({
  offeringId,
  marketingCode,
  apiBase,
  options,
  onClose,
  product,
  onCountryChange,
  promotionalCode = null,
  onApplyPromo,
}: Props) {
  // useMemo so the effect below does not see a new object on every render.
  const tracking = useMemo(() => options.tracking ?? {}, [options.tracking]);
  const purchaseFiredRef = useRef(false);

  const handleSuccess = (result: ContractSuccess) => {
    if (!purchaseFiredRef.current) {
      purchaseFiredRef.current = true;
      trackPurchase(tracking, {
        transactionId: result.basketId,
        value: product.value ?? 0,
        currency: product.currency,
        itemId: product.itemId,
        itemName: product.itemName,
      });
    }
    options.onSuccess?.(result);
  };

  const checkout = useSimpleCheckout({
    offeringId,
    marketingCode,
    apiBase,
    onSuccess: handleSuccess,
    onError: options.onError,
    promotionalCode,
  });

  const { values, setValue, submitting, error, success, canSubmit, handleSubmit } = checkout;

  // Newsletter opt-in lives outside the Compax contract values — it's a
  // separate Brevo subscription fired once after the order succeeds.
  const [newsletter, setNewsletter] = useState(false);
  const newsletterFiredRef = useRef(false);
  useEffect(() => {
    if (success && newsletter && !newsletterFiredRef.current) {
      newsletterFiredRef.current = true;
      void subscribeNewsletter(apiBase, {
        email: values.email,
        firstname: values.firstName,
        lastname: values.lastName,
        salutation: values.salutation ? SALUTATION_LABELS[values.salutation] : undefined,
        acceptMarketing: values.marketing,
      }).catch(() => {
        // Best-effort: never surface a newsletter failure on a completed order.
      });
    }
  }, [success, newsletter, apiBase, values]);

  // Fire add_payment_info once when the user starts submitting; the hook
  // flips `submitting` to true exactly at the moment of form submit.
  const paymentInfoFiredRef = useRef(false);
  useEffect(() => {
    if (submitting && !paymentInfoFiredRef.current) {
      paymentInfoFiredRef.current = true;
      trackAddPaymentInfo(tracking, { value: product.value, currency: product.currency });
    }
  }, [submitting, tracking, product.value, product.currency]);

  // Re-trigger the reprice flow once on mount, using the (possibly
  // persisted-from-localStorage) country. Without this, a buyer who
  // closed the modal with country=DE/CH and reopens later would briefly
  // see the AT-default price even though their form already has DE/CH
  // selected. App.tsx's handleCountryChange is a no-op on AT, so this is
  // safe for fresh modals where country is still the AT default.
  const initialCountrySyncedRef = useRef(false);
  useEffect(() => {
    if (initialCountrySyncedRef.current) return;
    initialCountrySyncedRef.current = true;
    onCountryChange?.(values.country);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (success) {
    return (
      <div className="px-6 py-12 text-center">
        <h2 className="mb-4 text-2xl font-bold text-black">Vielen Dank für Ihre Bestellung!</h2>
        <p className="mb-6 text-base text-gray-medium">
          Ihr WOMAN-Abo wurde erfolgreich bestellt. Sie erhalten in Kürze eine Bestätigungs-E-Mail sowie eine separate
          E-Mail zum Festlegen Ihres Passworts.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="bg-primary px-6 py-3 text-base font-semibold text-white hover:bg-primary-dark"
        >
          Schließen
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 p-6">
      {/* With gift orders in season the buyer needs to see that this block
          is where the parcel goes. */}
      <SectionHeading>Empfänger:innenadresse</SectionHeading>
      <select
        id="wco-salutation"
        aria-label="Anrede"
        value={values.salutation ?? ''}
        onChange={(e) => setValue('salutation', (e.target.value || null) as Salutation | null)}
        className={`${inputCls} appearance-none bg-no-repeat pr-10 ${values.salutation ? '' : 'text-gray-medium'}`}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%239ca3af' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 8 10 12 14 8'/></svg>\")",
          backgroundPosition: 'right 0.75rem center',
          backgroundSize: '1.25rem 1.25rem',
        }}
        required
      >
        <option value="" disabled>
          Anrede *
        </option>
        {Object.entries(SALUTATION_LABELS).map(([value, label]) => (
          <option key={value} value={value} className="text-black">
            {label}
          </option>
        ))}
      </select>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          id="wco-firstName"
          type="text"
          required
          autoComplete="given-name"
          placeholder="Vorname *"
          aria-label="Vorname"
          value={values.firstName}
          onChange={(e) => setValue('firstName', e.target.value)}
          className={inputCls}
        />
        <input
          id="wco-lastName"
          type="text"
          required
          autoComplete="family-name"
          placeholder="Nachname *"
          aria-label="Nachname"
          value={values.lastName}
          onChange={(e) => setValue('lastName', e.target.value)}
          className={inputCls}
        />
      </div>

      <input
        id="wco-email"
        type="email"
        required
        autoComplete="email"
        placeholder="E-Mail *"
        aria-label="E-Mail"
        value={values.email}
        onChange={(e) => setValue('email', e.target.value)}
        className={inputCls}
      />

      {/* Optional date of birth. Native date picker → ISO YYYY-MM-DD,
          which is what Compax accepts. Left blank = nothing sent; the
          server skips the field on write so a previously-set birthday
          isn't blanked by a partial update.

          Type swap (text ↔ date): HTML5 date inputs ignore `placeholder`
          and show the browser's format hint instead (often today's date),
          which confuses buyers about what the field is for. Rendering as
          a plain text input lets the "Geburtsdatum" placeholder show; we
          switch to type="date" on focus so the native picker still opens
          and the value stays an ISO string. */}
      <BirthdayInput value={values.birthday} onChange={(v) => setValue('birthday', v)} />

      <div className="grid grid-cols-[2fr_1fr] gap-3">
        <input
          id="wco-street"
          type="text"
          required
          autoComplete="address-line1"
          placeholder="Straße *"
          aria-label="Straße"
          value={values.street}
          onChange={(e) => setValue('street', e.target.value)}
          className={inputCls}
        />
        <input
          id="wco-houseNumber"
          type="text"
          required
          placeholder="Hausnr. *"
          aria-label="Hausnummer"
          value={values.houseNumber}
          onChange={(e) => setValue('houseNumber', e.target.value)}
          className={inputCls}
        />
      </div>

      <div className="grid grid-cols-[1fr_2fr] gap-3">
        <input
          id="wco-zip"
          type="text"
          required
          inputMode="numeric"
          autoComplete="postal-code"
          placeholder="PLZ *"
          aria-label="PLZ"
          value={values.zip}
          onChange={(e) => setValue('zip', e.target.value)}
          className={inputCls}
        />
        <input
          id="wco-city"
          type="text"
          required
          autoComplete="address-level2"
          placeholder="Ort *"
          aria-label="Ort"
          value={values.city}
          onChange={(e) => setValue('city', e.target.value)}
          className={inputCls}
        />
      </div>

      <select
        id="wco-country"
        aria-label="Land"
        value={values.country}
        onChange={(e) => {
          const next = e.target.value as Country;
          setValue('country', next);
          onCountryChange?.(next);
        }}
        className={`${inputCls} appearance-none bg-no-repeat pr-10`}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%239ca3af' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 8 10 12 14 8'/></svg>\")",
          backgroundPosition: 'right 0.75rem center',
          backgroundSize: '1.25rem 1.25rem',
        }}
        required
      >
        {(Object.keys(COUNTRY_LABELS) as Country[]).map((code) => (
          <option key={code} value={code} className="text-black">
            {COUNTRY_LABELS[code]}
          </option>
        ))}
      </select>

      <BillingAddressDisclosure
        open={values.hasSeparateBillingAddress}
        onToggle={() => setValue('hasSeparateBillingAddress', !values.hasSeparateBillingAddress)}
      />
      {values.hasSeparateBillingAddress && <BillingFields values={values} setValue={setValue} />}

      <PaymentMethodPicker value={values.paymentMethod} onChange={(v) => setValue('paymentMethod', v)} />

      {values.paymentMethod === 'INVOICE' && (
        <p
          // Same inline-styled, host-CSS-immune treatment as the Stripe
          // "verschlüsselt" hint below — keeps the typography consistent.
          style={{
            fontSize: '12px',
            lineHeight: 1.3,
            color: '#6b7280',
            margin: 0,
            padding: 0,
            fontFamily: 'inherit',
          }}
        >
          Prämien werden 14 Tage nach Zahlungseingang verschickt.
        </p>
      )}

      {values.paymentMethod === 'STRIPE' && (
        <>
          <CardBrandLogos />
          <div style={cardFieldStyle} aria-label="Kartennummer">
            <CardNumberElement options={{ style: cardStyle, placeholder: 'Kartennummer *', showIcon: true }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div style={cardFieldStyle} aria-label="Ablaufdatum">
              <CardExpiryElement options={{ style: cardStyle, placeholder: 'MM / JJ *' }} />
            </div>
            <div style={cardFieldStyle} aria-label="Prüfziffer">
              <CardCvcElement options={{ style: cardStyle, placeholder: 'CVC *' }} />
            </div>
          </div>
          <p
            // Inline styles (host-CSS-immune) plus a small lock glyph as a visual
            // reassurance that the card data goes straight to Stripe, encrypted.
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '12px',
              lineHeight: 1.3,
              color: '#6b7280',
              margin: 0,
              padding: 0,
              fontFamily: 'inherit',
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              aria-hidden="true"
              style={{ width: 12, height: 12, flexShrink: 0 }}
              fill="currentColor"
            >
              <path d="M8 1a3 3 0 0 0-3 3v3H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-1V4a3 3 0 0 0-3-3zm-2 6V4a2 2 0 1 1 4 0v3H6z" />
            </svg>
            Ihre Daten werden verschlüsselt übermittelt.
          </p>
        </>
      )}

      {onApplyPromo && <PromoCodeField applied={promotionalCode} onApply={onApplyPromo} />}

      <label className="flex cursor-pointer items-start gap-2 text-sm text-black">
        <input
          type="checkbox"
          checked={values.terms}
          onChange={(e) => setValue('terms', e.target.checked)}
          className="mt-1 shrink-0"
          required
        />
        <span>
          Ich akzeptiere die{' '}
          <a href="https://www.vgn.at/agb" target="_blank" rel="noopener noreferrer" className="text-coral underline">
            AGB
          </a>{' '}
          und die{' '}
          <a
            href="https://www.vgn.at/pdf/Datenschutzpolicy.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-coral underline"
          >
            Datenschutzerklärung
          </a>
          .
        </span>
      </label>

      {/*
        DSGVO-required affirmative marketing opt-in. Default unchecked.
        Same wording as the standard /abo/checkout flow, but the lengthy
        legal block is hidden behind a triangle by default to keep the
        modal short — the submit button stays close to the action.
      */}
      <NewsletterOptIn checked={newsletter} onChange={setNewsletter} />

      <MarketingOptIn checked={values.marketing} onChange={(v) => setValue('marketing', v)} />

      {error && <p className="bg-primary/10 p-3 text-sm text-primary">{error}</p>}

      <button
        type="submit"
        disabled={!canSubmit}
        className="mt-2 bg-primary px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? 'Bestellung wird verarbeitet …' : 'Zahlungspflichtig bestellen'}
      </button>
    </form>
  );
}
